import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import useTableData from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import { displayValue } from "@/utils/Helper";
import { Checkbox } from "@/components/ui/CheckBox";
import { ColumnDef } from "@tanstack/react-table";
import React from "react";
import { useToast } from "@/components/ui/UseToast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";
import { ErrorType } from "@/app/types";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { useGetAllCounterQuery } from "@/services/CounterApi";
import { CounterDetails } from "@/app/types/counter";
import { Status } from "@/app/shared_enums/enums";
import { Badge } from "@/components/ui/Badge";
import { DataTableRowActions } from "./DataTableRowAction";
import AssignCounter from "../modal/AssignCounter";
import { StatusFilter } from "@/constants/counter-management";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import CounterForm from "../modal/CounterForm";
import { Organization } from '../../../../../services/OrganizationApi';

const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "id",
};
const CounterManagementTable = () => {
  const counter = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).counter ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [openFilter, setOpenFilter] = React.useState(false);
  const [action, setAction] = React.useState<"add" | "edit">("add");
  const [filterData, setFilterData] = React.useState({});
  const [data, setData] = React.useState<CounterDetails | undefined>(undefined);
  const [assignCounter, setAssignCounter] = React.useState<boolean>(false);
  const [inputValue, setInputValue] = React.useState<string>("");

  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const {
    data: counterData,
    isLoading: isCounterDataLoading,
    refetch,
    error,
    isError,
  } = useGetAllCounterQuery(
    {
      query: query,
    },
    {
      skip: query == "",
    }
  );

  const { data: staffData } = useGetStaffListQuery({query:""});

  const tableData = React.useMemo(() => {
    return Array.isArray(counterData?.data) ? counterData?.data : [];
  }, [counterData?.data]);

  const handleOpenForm = (payload: CounterDetails) => {
    setAction("edit");
    setData({ ...payload, id: payload.id });
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<CounterDetails> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={counter}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
      />
    ),
  };

  const handleViewStaff = (data: CounterDetails) => {
    setData(data);
    setAssignCounter(true);
  };
  const columns: ColumnDef<CounterDetails>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "counter_name",
      meta: "counter_name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Counter No"
          sortKey="id"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const Value = row?.original.id;
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {counter === "write" ? (
              <button
                onClick={() => handleOpenForm(row.original)}
                className="underline"
              >
                {displayValue(Value)}
              </button>
            ) : (
              displayValue(Value)
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "counter_code",
      meta: "counter_code",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Counter Code"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis capitalize whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.code)}
          </div>
        );
      },
    },
    {
      accessorKey: "staff_list",
      meta: "staff_list",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Assigned Staff"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        
        const staffCount = row?.original?.staff_members?.length ?? 0;
        return (
          <div className="flex justify-start underline">
            <div
              className="flex text-xl items-center justify-center pl-10 text-black  cursor-pointer"
              onClick={() => handleViewStaff(row?.original)}
            >
              {String(displayValue(staffCount)).padStart(2, "0")}{" "}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <Badge
              className={`${row.original?.status == Status.Active ? "bg-green-600" : "bg-red-500"} text-white font-medium`}
            >
              {displayValue(row?.original.status)}
            </Badge>
          </div>
        );
      },
    },
    ...(counter !== "read" ? [actionsColumn] : []),
  ];
  const totalRecords = counterData?.filtered_count ?? 0;

  React.useEffect(() => {
    if (isError) {
      // error state
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterData((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        [filterName]: value,
      };
      return newFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Counter Status",
      options: StatusFilter,
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "multiselect",
      name: "staff_ids",
      label: "Staff",
      options: staffData?.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      function: (value: string) => handleFilterChange("staff_ids", value),
    },
  ];

  return (
    <>
      <div className="flex flex-row lg:items-center justify-between gap-4 px-3 ">
        {/* Table Filter component */}
        <TableSearchFilter
          setSearchCriteria={setSearchCriteria}
          type="number"
          inputValue={inputValue}
          setInputValue={setInputValue}
          placeHolder={"Search"}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={counter !== "read"}
            className="bg-primary text-sm  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={handleOpen}
          >
            <i className="fa-solid fa-plus" />
            {""}
            Create New
          </Button>
          {/* <DataTableViewOptions table={table} /> */}
          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="capitalize">click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isCounterDataLoading}
          searchMessage="No Counter Found"
        />{" "}
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}
      <AssignCounter
        isOpen={assignCounter}
        setOpen={setAssignCounter}
        data={data}
        setData={setData}
        refetch={refetch}
      />
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />
      <CounterForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </>
  );
};

export default CounterManagementTable;
